import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../../components/Table/Table";
import "./UomList.css";
import {
  getUOM,
  updateUomStatusApi,
  deleteUomApi,
} from "../../../../services/UOM/uom.service";
import { Uom } from "../../../../services/UOM/uom.types";
import { Pencil, Eye, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
const UomList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [selectedUom, setSelectedUom] = useState<Uom | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const calledOnce = useRef(false);

  const loadUoms = async () => {
    try {
      const data = await getUOM();
      setUoms(data);
    } catch (error) {
      console.error("Error fetching UOM:", error);
      setUoms([]);
    }
  };

  useEffect(() => {
    if (calledOnce.current) return;
    calledOnce.current = true;
    loadUoms();
  }, []);

const toggleStatus = async (id: number, currentStatus: string) => {
  const newStatus =
    currentStatus?.toLowerCase() === "active" ? "inactive" : "active";

  try {
    await updateUomStatusApi(id, { status: newStatus });

    const updated = uoms.map((u) =>
      u.id === id ? { ...u, status: newStatus } : u
    );

    setUoms(updated);
    toast.success(`Status updated successfully`);
  } catch (error) {
    alert("Failed to update status!");
  }
};
  const confirmDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteUomApi(deleteId);
      setUoms((prev) => prev.filter((u) => u.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const filtered = uoms.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedData = filtered
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  const columns = [
    { key: "serial", label: "S.NO" },
    { key: "name", label: "UOM" },
    { key: "description", label: "Description" },
    {
      key: "status",
      label: "Status",
      render: (value: string, row: Uom) => (
       <button
    className={`status-btn ${value?.toLowerCase()}`}
    onClick={() => toggleStatus(row.id, row.status)}
  >
    {value}
  </button>
      ),
    },
  ];

  return (
    <div className="category-container">
      <div className="header-bar">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="search-bar"
        />
        <button className="add-btn" onClick={() => navigate("/add/uom")}>
          Add UOM
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={paginatedData}
        tableClassName="compact-table"
        actions={(row) => (
          <div className="action-icons">
            <button
              className="icon-btn"
              title="View UOM"
              onClick={() => setSelectedUom(row)}
            >
              <Eye size={16} />
            </button>

            <button
              className="icon-btn"
              title="Edit UOM"
              onClick={() => navigate(`/edit/uom/${row.id}`)}
            >
              <Pencil size={16} />
            </button>

            <button
              className="icon-btn delete"
              title="Delete UOM"
              onClick={() => setDeleteId(row.id)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      />

      {/* Pagination */}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {/* View Modal */}
      {selectedUom && (
        <div className="modal-overlay" onClick={() => setSelectedUom(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>UOM Details</h3>
            <p><strong>Name:</strong> {selectedUom.name}</p>
            <p><strong>Status:</strong> {selectedUom.status}</p>
            <p><strong>Description:</strong> {selectedUom.description}</p>
            <button className="add-btn" onClick={() => setSelectedUom(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId !== null && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this UOM?</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button className="add-btn delete" onClick={confirmDelete}>
                Yes
              </button>
              <button className="add-btn" onClick={() => setDeleteId(null)}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UomList;
